import { useEffect, useState } from "react";

import { ChevronDown, ChevronUp } from "lucide-react";

import { ReactComponent as PauseIcon } from "@assets/svg/media-pause-filled.svg";
import { ReactComponent as PlayIcon } from "@assets/svg/media-play-filled.svg";

import { Button } from "@components/ui/button";

import { cn } from "@utils/ui";

import { useRecordingDock } from "./store/useRecordingDock";

const formatElapsed = (ms: number) => {
  const totalSeconds = Math.floor(ms / 1000);
  const hrs = Math.floor(totalSeconds / 3600);
  const mins = Math.floor((totalSeconds % 3600) / 60);
  const secs = totalSeconds % 60;
  return [hrs, mins, secs].map((n) => n.toString().padStart(2, "0")).join(":");
};

type RecordingDockProps = {
  /** Navigate back to the owning case's scribe tab (dock "Return"). */
  onReturnToSession: () => void;
  /** Stop + save the session (dock "Finish Recording"). */
  onFinish: () => void;
};

/**
 * Persistent floating recording dock. Renders bottom-right while a
 * live session is running and the user is off the owning case's scribe tab, so
 * recording status, timer, and quick controls stay in view during in-case
 * navigation. Reads the global recording store; the App wires the case-aware
 * navigation + stop callbacks.
 *
 * Visibility is decided by the caller (App) via `shouldShowDock` — this
 * component assumes it should render when mounted.
 */
export const RecordingDock = ({
  onReturnToSession,
  onFinish,
}: RecordingDockProps) => {
  const caseNumber = useRecordingDock((s) => s.caseNumber);
  const examineeName = useRecordingDock((s) => s.examineeName);
  const status = useRecordingDock((s) => s.status);
  const notes = useRecordingDock((s) => s.notes);
  const setNotes = useRecordingDock((s) => s.setNotes);
  const notesSaveState = useRecordingDock((s) => s.notesSaveState);
  const controls = useRecordingDock((s) => s.controls);
  const elapsedMs = useRecordingDock((s) => s.elapsedMs);

  const [notesOpen, setNotesOpen] = useState(true);
  // Local 1s tick so the timer refreshes off the store's clock while the scribe
  // page (and its own ticker) is unmounted.
  const [elapsed, setElapsed] = useState(() => elapsedMs());

  const isPaused = status === "paused";

  useEffect(() => {
    setElapsed(elapsedMs());
    if (status !== "recording") return;
    const interval = setInterval(() => setElapsed(elapsedMs()), 1000);
    return () => clearInterval(interval);
  }, [status, elapsedMs]);

  const notesLabel =
    notesSaveState === "saving"
      ? "Saving…"
      : notesSaveState === "saved"
        ? "Saved automatically"
        : notesSaveState === "error"
          ? "Save failed"
          : ""; // idle — nothing saved yet

  return (
    <div
      // Sits above the Bundle Splitter toast (bottom-4 right-4, z-50): offset up
      // so the two floating surfaces never overlap.
      className="fixed bottom-4 right-4 z-[60] w-[372px] max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl border border-border bg-background-white shadow-lg"
      role="dialog"
      aria-label="Assessment recording"
    >
      {/* Header — case-scoped title so it's always clear WHICH case is live. */}
      <div className="flex h-[45px] items-center justify-between gap-3 border-b border-border bg-background-raised px-4">
        <div className="flex min-w-0 flex-col leading-tight">
          <span className="truncate text-[13.5px] font-semibold text-foreground">
            Assessment recording
          </span>
          {(examineeName || caseNumber) && (
            <span className="truncate text-[11px] text-foreground-muted">
              {examineeName ?? "Case"} · {caseNumber}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={() => setNotesOpen((o) => !o)}
          title="Show or hide notes"
          aria-label={notesOpen ? "Hide notes" : "Show notes"}
          className="flex size-[26px] shrink-0 items-center justify-center rounded-md bg-background-white text-foreground-muted shadow-[inset_0_0_0_1px_hsl(var(--border))] hover:text-foreground"
        >
          {notesOpen ? (
            <ChevronDown className="size-4" />
          ) : (
            <ChevronUp className="size-4" />
          )}
        </button>
      </div>

      {/* Controls row: pause/resume + timer + status pill. */}
      <div className="flex flex-col gap-3 px-4 pb-3.5 pt-4">
        <div className="flex items-center gap-2.5">
          <button
            type="button"
            onClick={() => (isPaused ? controls.resume() : controls.pause())}
            aria-label={isPaused ? "Resume recording" : "Pause recording"}
            className="grid size-9 shrink-0 place-items-center rounded-full bg-primary text-primary-foreground transition-colors hover:bg-primary-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {isPaused ? (
              <PlayIcon className="size-4 [&_path]:fill-primary-foreground [&_path]:stroke-primary-foreground" />
            ) : (
              <PauseIcon className="size-4 [&_path]:fill-primary-foreground [&_path]:stroke-primary-foreground" />
            )}
          </button>
          <span className="font-mono text-base font-semibold tabular-nums text-foreground">
            {formatElapsed(elapsed)}
          </span>
          {/* Status pill mirrors the in-panel ScribeSession pill (design tokens). */}
          <span
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium",
              isPaused
                ? "bg-warning-50 text-warning-700"
                : "bg-success-50 text-success-700"
            )}
          >
            <span
              className={cn(
                "size-1.5 rounded-full",
                isPaused ? "bg-warning-700" : "animate-pulse bg-success-700"
              )}
              aria-hidden="true"
            />
            {isPaused ? "Paused" : "Recording"}
          </span>
        </div>
      </div>

      {/* Assessment Notes — the SAME notes shown/edited on the scribe tab
 (shared store value), autosaved by the RecordingHost. */}
      {notesOpen && (
        <div className="flex flex-col gap-2 border-t border-border px-4 pb-3.5 pt-3">
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs font-semibold text-foreground">
              Assessment Notes
            </span>
            <span
              className={cn(
                "text-[11px]",
                notesSaveState === "error"
                  ? "text-destructive"
                  : "text-foreground-muted"
              )}
            >
              {notesLabel}
            </span>
          </div>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Note something to follow up…"
            aria-label="Assessment notes"
            rows={3}
            className="w-full resize-none rounded-lg border border-border bg-transparent px-3 py-2 text-[12.5px] leading-relaxed text-foreground outline-none placeholder:text-foreground-muted"
          />
        </div>
      )}

      {/* Footer actions. */}
      <div className="flex items-center justify-end gap-1.5 border-t border-border bg-background-raised px-4 py-2.5">
        <Button
          variant="ghost"
          size="sm"
          onClick={onReturnToSession}
          className="text-foreground hover:bg-foreground/[0.06]"
        >
          Return to session
        </Button>
        <Button size="sm" onClick={onFinish}>
          Finish Recording
        </Button>
      </div>
    </div>
  );
};
